import { z } from 'zod';

import { VENUES, type Venue } from './calendar.ts';
import { idSchema, type TradeType } from './primitives.ts';
import { DAY_MS, istMidnight, todayInIst } from './when.ts';

const UNDERLYING_MAX_LENGTH = 40;

const TRADINGSYMBOL_MAX_LENGTH = 40;

// Within this window of front-month expiry, the default future (and autopilot's) is the next month.
const NEXT_MONTH_DEFAULT_MS = 10 * DAY_MS;

export function notExpiring(expiry: string | Date | null, now: Date): boolean {
  if (expiry === null) return true;
  const day = expiry instanceof Date ? expiry.getTime() : new Date(expiry).getTime();
  return !Number.isFinite(day) || day - now.getTime() >= NEXT_MONTH_DEFAULT_MS;
}

export const MARKETS = ['EQUITY', 'INDEX', 'COMMODITY'] as const;
export type Market = (typeof MARKETS)[number];

export const INSTRUMENT_KINDS = ['CASH', 'FUTURE', 'OPTION'] as const;
export type InstrumentKind = (typeof INSTRUMENT_KINDS)[number];

export const OPTION_TYPES = ['CE', 'PE'] as const;
export type OptionType = (typeof OPTION_TYPES)[number];

// Never rebuild a leg's name from other fields: the bare product hides expiry and lot variant.
export const legNameOf = (leg: { tradingsymbol: string }): string => leg.tradingsymbol;

export function optionTypeOf(tradingsymbol: string): OptionType | null {
  if (tradingsymbol.endsWith('PE')) return 'PE';
  if (tradingsymbol.endsWith('CE')) return 'CE';
  return null;
}

/** A dated contract: a cash name can end in CE (RELIANCE). */
export const isOptionLeg = (leg: { tradingsymbol: string; expiry: string | null }): boolean =>
  leg.expiry !== null && optionTypeOf(leg.tradingsymbol) !== null;

/**
 * `expiry`: a full day, a day+month (year from listed expiries), a month for its last expiry (the
 * monthly), or absent for the nearest.
 */
export interface OptionSpec {
  strike: number;
  optionType: OptionType;
  expiry?: string | { day: number; month: number } | MonthAsked;
}

type MonthAsked = { month: number; year?: number };

/**
 * The series an analyst names: a future's month, the Nth listed as a continuous chart counts
 * them, or `spot` for the name's own price, an index's spot or a stock's cash. With no year a month
 * is its nearest, ahead or just gone.
 */
export type SeriesAsked = MonthAsked | { nth: number } | 'spot';

const MONTH_NAMES = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december',
] as const;

/** `spot`, `1!`, or a month: `oct`, `october`, `oct26`, `oct-2026`, `nov'26`, `10` or `10/26`. */
export function parseSeries(raw: string): SeriesAsked | null {
  const written = raw.trim().toLowerCase();
  if (written === 'spot') return 'spot';
  const nth = /^([1-9])!$/.exec(written)?.[1];
  if (nth !== undefined) return { nth: Number(nth) };
  const found = /^(?:([a-z]{3,9})|(\d{1,2})(?=[-/'’]|$))(?:[-/'’]?(\d{4}|\d{2}))?$/.exec(written);
  if (!found) return null;
  const [, word, digits, year] = found;
  const month =
    word === undefined
      ? Number(digits)
      : MONTH_NAMES.findIndex((name) => name.startsWith(word)) + 1;
  if (month < 1 || month > 12) return null;
  if (year === undefined) return { month };
  return { month, year: year.length === 2 ? 2000 + Number(year) : Number(year) };
}

export function monthNamed(raw: string): MonthAsked | null {
  if (!/^[a-z]/i.test(raw)) return null;
  const series = parseSeries(raw);
  return typeof series === 'object' && series !== null && 'month' in series ? series : null;
}

export function seriesLabel(series: SeriesAsked): string {
  if (series === 'spot') return 'spot';
  if ('nth' in series) return `${series.nth}!`;
  const name = MONTH_NAMES[series.month - 1] ?? '';
  const word = name.charAt(0).toUpperCase() + name.slice(1, 3);
  return series.year === undefined ? word : `${word} ${series.year}`;
}

function optionView(underlying: string, tradeType: TradeType, optionType: OptionType): string {
  const bullish = (tradeType === 'LONG') === (optionType === 'CE');
  const held = `a ${tradeType === 'LONG' ? 'bought' : 'sold'} ${optionType === 'CE' ? 'call' : 'put'}`;
  return bullish
    ? `🐂 Bullish on ${underlying} — ${held}`
    : `🐻 Bearish on ${underlying} — ${held}`;
}

/** LONG/SHORT alone misleads on an option: a bought put is bearish. Null off an option. */
export function optionViewOf(recommendation: {
  trackingKind: string;
  trackingTradingsymbol: string;
  underlying: string;
  tradeType: TradeType;
}): string | null {
  if (recommendation.trackingKind !== 'OPTION') return null;
  const side = optionTypeOf(recommendation.trackingTradingsymbol);
  return side === null
    ? null
    : optionView(recommendation.underlying, recommendation.tradeType, side);
}

export interface Underlying {
  name: string;
  market: Market;
}

export interface TrackedContract {
  token: number;
  tradingsymbol: string;
  exchange: Venue;
  contract: string;
  kind: InstrumentKind;
  expiry: string | null;
  tickSize: number;
}

export interface InstrumentRef extends TrackedContract {
  lotSize: number;
}

const underlyingNameSchema = z
  .string()
  .trim()
  .min(1)
  .max(UNDERLYING_MAX_LENGTH)
  .regex(/^[A-Za-z0-9&._-]+$/, 'an underlying must be a single symbol, e.g. RELIANCE')
  .transform((s) => s.toUpperCase());

export const underlyingSchema = z.object({
  name: underlyingNameSchema,
  market: z.enum(MARKETS),
}) satisfies z.ZodType<Underlying>;

export function underlyingOf(row: { underlying: string; market: Market }): Underlying {
  return { name: row.underlying, market: row.market };
}

export interface Taking {
  kind: InstrumentKind;
  token: number;
  /** Root name (e.g. SILVERM); ranks the tracked family first among lot-size variants. */
  contract: string;
  tradeType: TradeType;
}

function trackingOf(row: {
  trackingKind: InstrumentKind;
  trackingToken: number;
  trackingContract: string;
  tradeType: TradeType;
}): Taking {
  return {
    kind: row.trackingKind,
    token: row.trackingToken,
    contract: row.trackingContract,
    tradeType: row.tradeType,
  };
}

export async function tradeableLegs(
  listing: {
    instruments(): Promise<{
      tradeable(underlying: Underlying, now: Date, taking: Taking): Instrument[];
    }>;
  },
  recommendation: Parameters<typeof underlyingOf>[0] & Parameters<typeof trackingOf>[0],
  now: Date,
): Promise<Instrument[]> {
  const listed = await listing.instruments();
  return listed.tradeable(underlyingOf(recommendation), now, trackingOf(recommendation));
}

const trackedContractShape = {
  token: idSchema,
  tradingsymbol: z.string().trim().min(1).max(TRADINGSYMBOL_MAX_LENGTH),
  exchange: z.enum(VENUES),
  contract: z.string().trim().min(1).max(TRADINGSYMBOL_MAX_LENGTH),
  kind: z.enum(INSTRUMENT_KINDS),
  expiry: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'expiry must be an ISO day (yyyy-mm-dd)')
    .nullable(),
  tickSize: z.number().positive(),
};

export const trackedContractSchema = z.object(
  trackedContractShape,
) satisfies z.ZodType<TrackedContract>;

export const instrumentRefSchema = z.object({
  ...trackedContractShape,
  lotSize: z.number().int().positive(),
}) satisfies z.ZodType<InstrumentRef>;

export function validateRoll(
  from: { token: number; contract: string; expiry: string; lotSize?: number },
  to: {
    token: number;
    contract: string;
    expiry: string | null;
    tradingsymbol: string;
    lotSize?: number;
  },
  subject: string,
): string[] {
  const violations: string[] = [];
  if (to.contract !== from.contract) {
    violations.push(`a roll stays on the same contract: ${from.contract}, not ${to.contract}`);
  }
  if (to.expiry === null || to.expiry <= from.expiry) {
    violations.push(
      `${to.tradingsymbol} does not expire after the contract ${subject} is on (${from.expiry})`,
    );
  }
  if (to.token === from.token) violations.push(`${subject} is already on that contract`);
  if (from.lotSize !== undefined && to.lotSize !== undefined && to.lotSize !== from.lotSize) {
    violations.push(
      `${to.tradingsymbol} trades in lots of ${to.lotSize}, not ${from.lotSize} — its lot ` +
        'changed, so it cannot be rolled without re-sizing',
    );
  }
  return violations;
}

export interface Instrument {
  token: number;
  tradingsymbol: string;
  exchange: Venue;
  lotSize: number;
  tickSize: number;
  expiry: Date | null;
  kind: InstrumentKind;
  contract: string;
  option?: { strike: number; optionType: OptionType };
}

export function expiryDay(expiry: Date | null): string | null {
  return expiry ? todayInIst(expiry) : null;
}

// Inverse of toInstrument's contract naming; keep in sync. Only an option's contract equals its tradingsymbol.
function pinnedKind(pinned: {
  expiry: string | null;
  contract: string;
  tradingsymbol: string;
}): InstrumentKind {
  if (pinned.expiry === null) return 'CASH';
  return pinned.contract === pinned.tradingsymbol ? 'OPTION' : 'FUTURE';
}

export function pinTracking(instrument: Instrument): TrackedContract {
  return {
    token: instrument.token,
    tradingsymbol: instrument.tradingsymbol,
    exchange: instrument.exchange,
    contract: instrument.contract,
    kind: instrument.kind,
    expiry: expiryDay(instrument.expiry),
    tickSize: instrument.tickSize,
  };
}

export function pin(instrument: Instrument): InstrumentRef {
  return {
    ...pinTracking(instrument),
    lotSize: instrument.lotSize,
  };
}

// Never consults the dump, so contracts that have since expired still resolve.
export function unpin(pinned: {
  instrumentToken: number;
  tradingsymbol: string;
  exchange: Venue;
  contract: string;
  lotSize: number;
  tickSize: number;
  expiry: string | null;
}): Instrument {
  return {
    token: pinned.instrumentToken,
    tradingsymbol: pinned.tradingsymbol,
    exchange: pinned.exchange,
    lotSize: pinned.lotSize,
    tickSize: pinned.tickSize,
    expiry: pinned.expiry === null ? null : istMidnight(pinned.expiry),
    kind: pinnedKind(pinned),
    contract: pinned.contract,
  };
}
